import React, { useEffect, useRef, useState } from 'react'

import styles from './styles.module.scss'
import { type YouTubePlayerInstance, YouTubeState, loadYouTubeApi } from './youtubeApi'

type YouTubePlayerProps = {
  videoId: string
  title: string
  posterImage?: string
}

type PlayerStatus = 'loading' | 'playing' | 'paused' | 'buffering' | 'ended' | 'error'

type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null
  webkitFullscreenEnabled?: boolean
  webkitExitFullscreen?: () => Promise<void>
}

type FullscreenElement = HTMLElement & { webkitRequestFullscreen?: () => Promise<void> }

const SEEK_STEP = 5
// YouTube hides its own overlay (title, share, logo) after this much inactivity during playback.
const OVERLAY_IDLE_DELAY = 3000
const AUTOPLAY_FALLBACK_DELAY = 3000

const formatTime = (value: number) => {
  const total = Math.max(0, Math.floor(value || 0))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = String(total % 60).padStart(2, '0')
  return hours ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}` : `${minutes}:${seconds}`
}

const fullscreenElement = () => {
  const doc = document as FullscreenDocument
  return doc.fullscreenElement || doc.webkitFullscreenElement || null
}

const VolumeIcon = ({ muted }: { muted: boolean }) => (
  <svg viewBox="0 0 24 24" aria-hidden>
    <path d="M3 9.5h4l5-4.5v14l-5-4.5H3v-5Z" fill="currentColor" />
    {muted ? (
      <path d="m16 9.5 5 5m0-5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    ) : (
      <path
        d="M15.5 8.5a4.5 4.5 0 0 1 0 7m2.5-10a8 8 0 0 1 0 13"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    )}
  </svg>
)

const FullscreenIcon = ({ active }: { active: boolean }) => (
  <svg viewBox="0 0 24 24" aria-hidden>
    <path
      d={
        active
          ? 'M4 10h6V4M20 14h-6v6M10 10 4 4M14 14l6 6'
          : 'M4 10V4h6M20 14v6h-6M4 4l6 6M20 20l-6-6'
      }
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const YouTubePlayer = ({ videoId, title, posterImage }: YouTubePlayerProps) => {
  const rootRef = useRef<HTMLDivElement>(null)
  const frameHostRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<YouTubePlayerInstance | null>(null)
  const [status, setStatus] = useState<PlayerStatus>('loading')
  const [isReady, setIsReady] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [loadedFraction, setLoadedFraction] = useState(0)
  const [muted, setMuted] = useState(false)
  const [overlayVisible, setOverlayVisible] = useState(true)
  const overlayTimerRef = useRef<number>()
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [canFullscreen] = useState(() => {
    const doc = document as FullscreenDocument
    return Boolean(doc.fullscreenEnabled || doc.webkitFullscreenEnabled)
  })
  // iPhone Safari has no element Fullscreen API, so there the player covers the viewport instead.
  const [isViewportFullscreen, setIsViewportFullscreen] = useState(false)

  const isPlaying = status === 'playing' || status === 'buffering'

  useEffect(() => {
    const frameHost = frameHostRef.current
    if (!frameHost) return
    // The API replaces its target element with an iframe, so give it a node React does not own.
    const target = document.createElement('div')
    frameHost.appendChild(target)
    let cancelled = false
    let autoplayTimer: number | undefined

    loadYouTubeApi()
      .then((YT) => {
        if (cancelled) return
        const player = new YT.Player(target, {
          videoId,
          width: '100%',
          height: '100%',
          playerVars: {
            autoplay: 1,
            cc_load_policy: 0,
            controls: 0,
            disablekb: 1,
            fs: 0,
            iv_load_policy: 3,
            playsinline: 1,
            rel: 0,
            origin: window.location.origin,
          },
          events: {
            onReady: ({ target: player }) => {
              setIsReady(true)
              setDuration(player.getDuration())
              setMuted(player.isMuted())
              player.playVideo()
              // Browsers may block autoplay (notably iOS); fall back to the resume button.
              autoplayTimer = window.setTimeout(
                () => setStatus((current) => (current === 'loading' ? 'paused' : current)),
                AUTOPLAY_FALLBACK_DELAY
              )
            },
            onStateChange: ({ target: player, data }) => {
              // YouTube can switch auto-captions on from viewer preferences; the design has no caption toggle.
              if (data === YouTubeState.playing) player.unloadModule('captions')
              setCurrentTime(player.getCurrentTime())
              setDuration(player.getDuration())
              if (data === YouTubeState.playing) setStatus('playing')
              else if (data === YouTubeState.paused) setStatus('paused')
              else if (data === YouTubeState.buffering) setStatus('buffering')
              else if (data === YouTubeState.ended) setStatus('ended')
              else if (data === YouTubeState.cued) setStatus('paused')
            },
            onError: () => setStatus('error'),
          },
        })
        const iframe = player.getIframe()
        iframe.title = title
        iframe.tabIndex = -1
        playerRef.current = player
      })
      .catch(() => {
        if (!cancelled) setStatus('error')
      })

    return () => {
      cancelled = true
      window.clearTimeout(autoplayTimer)
      playerRef.current?.destroy()
      playerRef.current = null
      frameHost.replaceChildren()
    }
  }, [videoId, title])

  useEffect(() => {
    if (status !== 'playing') return
    const interval = window.setInterval(() => {
      const player = playerRef.current
      if (!player) return
      setCurrentTime(player.getCurrentTime())
      setLoadedFraction(player.getVideoLoadedFraction())
    }, 250)
    return () => window.clearInterval(interval)
  }, [status])

  useEffect(() => {
    const onChange = () => setIsFullscreen(fullscreenElement() === rootRef.current)
    document.addEventListener('fullscreenchange', onChange)
    document.addEventListener('webkitfullscreenchange', onChange)
    return () => {
      document.removeEventListener('fullscreenchange', onChange)
      document.removeEventListener('webkitfullscreenchange', onChange)
    }
  }, [])

  // Pointer moves inside the cross-origin iframe never reach this page, so the controls follow
  // YouTube's own overlay rules instead: shown on hover and while paused, hidden after a few
  // idle seconds of playback or as soon as the pointer leaves the player.
  const showOverlay = () => {
    setOverlayVisible(true)
    window.clearTimeout(overlayTimerRef.current)
    overlayTimerRef.current = window.setTimeout(() => setOverlayVisible(false), OVERLAY_IDLE_DELAY)
  }

  const hideOverlay = () => {
    window.clearTimeout(overlayTimerRef.current)
    setOverlayVisible(false)
  }

  useEffect(() => {
    if (status === 'playing') showOverlay()
  }, [status])

  useEffect(() => () => window.clearTimeout(overlayTimerRef.current), [])

  // A click inside YouTube's iframe (share dialog, logo, the video) moves focus into it;
  // step aside so our controls never cover YouTube's own dialogs.
  useEffect(() => {
    const onBlur = () => {
      if (document.activeElement === playerRef.current?.getIframe()) hideOverlay()
    }
    window.addEventListener('blur', onBlur)
    return () => window.removeEventListener('blur', onBlur)
  }, [])

  const togglePlay = () => {
    const player = playerRef.current
    if (!player) return
    if (isPlaying) {
      player.pauseVideo()
      return
    }
    if (status === 'ended') player.seekTo(0, true)
    player.playVideo()
  }

  const seek = (seconds: number) => {
    const player = playerRef.current
    if (!player || !duration) return
    const next = Math.min(Math.max(seconds, 0), duration)
    setCurrentTime(next)
    player.seekTo(next, true)
  }

  const toggleMute = () => {
    const player = playerRef.current
    if (!player) return
    if (muted) player.unMute()
    else player.mute()
    setMuted(!muted)
  }

  useEffect(() => {
    if (!isViewportFullscreen) return
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsViewportFullscreen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = overflow
      document.removeEventListener('keydown', onKey)
    }
  }, [isViewportFullscreen])

  const toggleFullscreen = () => {
    if (!canFullscreen) {
      setIsViewportFullscreen((active) => !active)
      return
    }
    const doc = document as FullscreenDocument
    const root = rootRef.current as FullscreenElement | null
    if (!root) return
    if (fullscreenElement()) {
      void (doc.exitFullscreen?.() ?? doc.webkitExitFullscreen?.())
    } else {
      void (root.requestFullscreen?.() ?? root.webkitRequestFullscreen?.())
    }
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const isRange = (event.target as HTMLElement).tagName === 'INPUT'
    const isButton = (event.target as HTMLElement).tagName === 'BUTTON'

    if (event.key === 'k' || (event.key === ' ' && !isButton)) {
      event.preventDefault()
      togglePlay()
    } else if (event.key === 'm') {
      toggleMute()
    } else if (event.key === 'f') {
      toggleFullscreen()
    } else if (!isRange && event.key === 'ArrowLeft') {
      event.preventDefault()
      seek(currentTime - SEEK_STEP)
    } else if (!isRange && event.key === 'ArrowRight') {
      event.preventDefault()
      seek(currentTime + SEEK_STEP)
    }
  }

  const progress = duration ? (currentTime / duration) * 100 : 0
  const controlsHidden = status === 'playing' && !overlayVisible

  return (
    <div
      ref={rootRef}
      className={styles.root}
      data-status={status}
      data-controls-hidden={controlsHidden}
      data-fullscreen={isFullscreen || isViewportFullscreen}
      data-viewport-fullscreen={isViewportFullscreen}
      onMouseEnter={showOverlay}
      onMouseLeave={hideOverlay}
      onPointerDown={showOverlay}
      onKeyDown={(event) => {
        showOverlay()
        onKeyDown(event)
      }}
    >
      <div ref={frameHostRef} className={styles.frame} />

      {/* YouTube renders nothing until its iframe boots, so keep the poster until then. */}
      {((!isReady && status !== 'error') || status === 'ended') && posterImage ? (
        <img className={styles.poster} src={posterImage} alt="" />
      ) : null}

      {status === 'error' ? (
        <p className={styles.error}>Video is unavailable. Please try again later.</p>
      ) : (
        // Every control sits where YouTube's native control of the same kind sits.
        <div className={styles.controls}>
          <button
            className={`${styles.iconButton} ${styles.volume}`}
            type="button"
            aria-label={muted ? 'Unmute' : 'Mute'}
            onClick={toggleMute}
          >
            <VolumeIcon muted={muted} />
          </button>

          <span className={styles.time}>
            {formatTime(currentTime)}
            <span className={styles.duration}> / {formatTime(duration)}</span>
          </span>

          <button
            className={`${styles.iconButton} ${styles.fullscreen}`}
            type="button"
            aria-label={isFullscreen || isViewportFullscreen ? 'Exit full screen' : 'Full screen'}
            onClick={toggleFullscreen}
          >
            <FullscreenIcon active={isFullscreen || isViewportFullscreen} />
          </button>

          <input
            className={styles.progress}
            type="range"
            min={0}
            max={duration || 0}
            step={0.1}
            value={Math.min(currentTime, duration || 0)}
            aria-label="Seek"
            aria-valuetext={`${formatTime(currentTime)} of ${formatTime(duration)}`}
            style={
              {
                '--progress': `${progress}%`,
                '--loaded': `${Math.max(progress, loadedFraction * 100)}%`,
              } as React.CSSProperties
            }
            disabled={!duration}
            onChange={(event) => seek(Number(event.target.value))}
          />
        </div>
      )}
    </div>
  )
}

export default YouTubePlayer
